import { TrendingUp } from 'lucide-react';
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure';
import type { AdminManagerCrmLeaderboard } from '../../api';

type RowStats = {
  revenue_month: number;
  active_orders: number;
  total_orders: number;
  delivered: number;
};

type Props = {
  loading: boolean;
  data: AdminManagerCrmLeaderboard | null;
  statsManagerId: number | null;
  statsUnassignedOnly?: boolean;
  onSelectManager: (managerId: number | null) => void;
  onSelectUnassigned?: () => void;
  onOpenOrders: (managerId: number | null) => void;
  managerLabel: (m?: { first_name?: string | null; username?: string | null } | null) => string;
  hapticSelection: () => void;
};

function LeaderboardRow({
  label,
  row,
  managerId,
  color,
  selected,
  maxRev,
  onSelectManager,
  onOpenOrders,
  hapticSelection,
}: {
  label: string;
  row: RowStats;
  managerId: number | null;
  color: string;
  selected: boolean;
  maxRev: number;
  onSelectManager: (managerId: number | null) => void;
  onOpenOrders: (managerId: number | null) => void;
  hapticSelection: () => void;
}) {
  const width = Math.max(8, ((row.revenue_month || 0) / maxRev) * 100);
  return (
    <div
      className="rounded-xl p-3"
      style={{
        background: selected ? 'rgba(246,173,85,0.08)' : 'rgba(255,255,255,0.03)',
        border: selected ? '1px solid rgba(246,173,85,0.35)' : '1px solid var(--border-subtle)',
      }}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs font-bold truncate" style={{ color: 'var(--text-1)' }}>{label}</p>
        <p className="text-xs font-bold shrink-0" style={{ color }}>
          {Math.round(row.revenue_month || 0).toLocaleString('ru-RU')} ₽
        </p>
      </div>
      <div className="h-2 rounded-full overflow-hidden mb-2" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${width}%`, background: color }} />
      </div>
      <div className="flex flex-wrap gap-2 text-[10px] mb-2" style={{ color: 'var(--text-3)' }}>
        <span>заказов {row.total_orders}</span>
        <span>активных {row.active_orders}</span>
        <span>доставлено {row.delivered}</span>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          className="press flex-1 py-1.5 rounded-lg text-[10px] font-semibold"
          style={{ background: 'rgba(99,179,237,0.12)', color: 'var(--cyan)', border: '1px solid var(--border-glass)' }}
          onClick={() => {
            hapticSelection();
            onSelectManager(managerId);
          }}
        >
          KPI
        </button>
        <button
          type="button"
          className="press flex-1 py-1.5 rounded-lg text-[10px] font-semibold"
          style={{ background: 'rgba(246,173,85,0.12)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.35)' }}
          onClick={() => {
            hapticSelection();
            onOpenOrders(managerId);
          }}
        >
          Заказы →
        </button>
      </div>
    </div>
  );
}

export function ManagerCrmLeaderboardPanel({
  loading,
  data,
  statsManagerId,
  statsUnassignedOnly = false,
  onSelectManager,
  onSelectUnassigned,
  onOpenOrders,
  managerLabel,
  hapticSelection,
}: Props) {
  const maxRev = Math.max(
    1,
    ...(data?.managers.map((m) => m.revenue_month || 0) ?? []),
    data?.unassigned.revenue_month ?? 0,
  );

  return (
    <AdminGlassDisclosure
      accent="amber"
      title="Продажи по менеджерам"
      subtitle="Выручка за 30 дней · сравнение команды"
      icon={<TrendingUp size={16} color="#F6AD55" />}
      badge={data?.managers.length ?? 0}
    >
      {loading ? (
        <p className="text-xs" style={{ color: 'var(--text-3)' }}>Считаем…</p>
      ) : !data ? (
        <p className="text-xs" style={{ color: 'var(--text-3)' }}>Нет данных</p>
      ) : (
        <div className="space-y-2">
          {data.managers.map((row) => {
            const mid = row.manager?.id;
            if (mid == null) return null;
            return (
              <LeaderboardRow
                key={mid}
                label={managerLabel(row.manager)}
                row={row}
                managerId={mid}
                color="#F6AD55"
                selected={statsManagerId === mid && !statsUnassignedOnly}
                maxRev={maxRev}
                onSelectManager={onSelectManager}
                onOpenOrders={onOpenOrders}
                hapticSelection={hapticSelection}
              />
            );
          })}
          <LeaderboardRow
            label="Без менеджера"
            row={data.unassigned}
            managerId={null}
            color="#FC814A"
            selected={statsUnassignedOnly}
            maxRev={maxRev}
            onSelectManager={() => onSelectUnassigned?.()}
            onOpenOrders={onOpenOrders}
            hapticSelection={hapticSelection}
          />
        </div>
      )}
    </AdminGlassDisclosure>
  );
}
